import { useEffect, useSyncExternalStore } from 'react';
import { action, subscribe } from './api.ts';

/**
 * Whether the signed-in person has a shift now (or starting within half an hour) and has not
 * clocked in, for the reminder under the top bar. One request for the whole app, refreshed
 * every two minutes and when the clock or the rota changes.
 */

export interface ClockDue {
  start: string;
  end: string;
  started: boolean;
}
interface MeLite {
  open: unknown;
  upcoming: { from: string; to: string; start: string; end: string }[];
}

let due: ClockDue | null = null;
const listeners = new Set<() => void>();
let users = 0;
let stop: (() => void) | null = null;
let timer: ReturnType<typeof setInterval> | null = null;

function publish(next: ClockDue | null) {
  if (JSON.stringify(next) === JSON.stringify(due)) return;
  due = next;
  for (const l of listeners) l();
}

async function refresh() {
  try {
    const me = await action<MeLite>('team.me', {});
    const now = Date.now();
    const shift = me.open ? undefined : me.upcoming.find((s) => Date.parse(s.from) - 30 * 60_000 <= now && now < Date.parse(s.to));
    publish(shift ? { start: shift.start, end: shift.end, started: Date.parse(shift.from) <= now } : null);
  } catch {
    // Offline or signed out: no reminder.
  }
}

export function useClockDue(enabled: boolean): ClockDue | null {
  const value = useSyncExternalStore(
    (l) => {
      listeners.add(l);
      return () => listeners.delete(l);
    },
    () => due,
  );
  useEffect(() => {
    if (!enabled) return;
    users++;
    if (!stop) {
      void refresh();
      stop = subscribe(['team'], () => void refresh());
      timer = setInterval(() => void refresh(), 120_000);
    }
    return () => {
      users--;
      if (users === 0) {
        stop?.();
        stop = null;
        if (timer) clearInterval(timer);
        timer = null;
      }
    };
  }, [enabled]);
  return enabled ? value : null;
}

/** Look again now (after clocking in or out, or publishing the rota). */
export const refreshTeam = () => void refresh();
